import React, { useState } from 'react';
import { Avatar } from 'antd';
import {
    ApplePreviewCard,
    AppleHeader,
    AppleContent,
    ArtistImage,
    ArtistInfo,
    ProductCard,
    AnimatedApplePlayer,
} from './styled';  // We'll create Apple-specific styled components
import { faApple } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const AppleCardComponent = ({ item, theme, onCardClick }) => {
    const [showPlayer, setShowPlayer] = useState(false);

    const cardClassName =
        theme?.card_style === 'fill-slightly-rounded'
            ? 'card-fill-slightly-rounded'
            : 'card-default';

    const handleClick = () => {
        // If displayOption === 'direct' or prerelease, open the link in new tab
        if (item.displayOption === 'direct' || item.appleType === 'prerelease') {
            window.open(item.url || item.appleUrl, '_blank');
            return;
        }
        // Otherwise, toggle embedded player
        setShowPlayer(!showPlayer);
        if (onCardClick) {
            onCardClick(item);
        }
    };

    // Function to render Apple Music embed if displayOption !== 'direct'
    const renderAppleEmbed = (height) => {
        // If displayOption === 'direct' or item.appleType === 'prerelease', skip the iframe
        if (item.displayOption === 'direct' || item.appleType === 'prerelease') {
            return null;
        }

        // Apple embed generally looks like:
        //   https://embed.music.apple.com/{country}/{album|playlist}/{appleId}?app=music
        // Adjust {country}, e.g. "us" or from item.storefront if you store that
        // If item.appleType === 'album' => /album/...
        // If item.appleType === 'playlist' => /playlist/...
        // If item.appleType === 'track' => /album/.../someTrackID  (less common)
        // Because Apple doesn't embed "artist" the same way, handle if needed.

        let embedType = item.appleType || 'album';
        // E.g. if item.appleType === 'artist', Apple doesn't do a direct "artist embed."
        if (embedType === 'artist') embedType = 'album';
        // Or handle differently if you want.

        return (
            <iframe
                allow="autoplay *; encrypted-media *;"
                frameBorder="0"
                height={height}
                style={{ width: '100%', overflow: 'hidden', background: 'transparent' }}
                sandbox="allow-forms allow-popups allow-pointer-lock allow-same-origin allow-scripts allow-top-navigation-by-user-activation"
                src={`https://embed.music.apple.com/us/${embedType}/${item.appleId}?app=music`}
            />
        );
    };

    const renderCard = () => {
        // Button layout, highlight, etc. → same logic as Spotify but replaced references
        if (item.cardType === 'button') {
            return (
                <div>
                    <ApplePreviewCard
                        theme={theme}
                        className="button-layout"
                        onClick={handleClick}
                        role="button"
                        tabIndex={0}
                    >
                        <AppleContent layout="button" className="button-layout">
                            <ArtistImage
                                src={item.thumbnail || 'https://via.placeholder.com/150'}
                                alt={item.title}
                                className="button-image"
                            />
                            <ArtistInfo layout="button" theme={theme}>
                                <p className="button-name">{item.buttonName}</p>
                            </ArtistInfo>
                            <FontAwesomeIcon
                                icon={faApple}
                                className="apple-logo"
                                style={{
                                    fontSize: '30px',
                                    color: theme?.card_text_color || '#000',
                                }}
                            />
                        </AppleContent>
                    </ApplePreviewCard>
                    <AnimatedApplePlayer show={showPlayer} style={{ marginTop: '20px' }}>
                        {renderAppleEmbed('475px')}
                    </AnimatedApplePlayer>
                </div>
            );
        }

        if (item.cardType === 'highlight') {
            return (
                <div>
                    <ApplePreviewCard
                        theme={theme}
                        className="highlight-layout"
                        onClick={handleClick}
                        role="button"
                        tabIndex={0}
                    >
                        <AppleHeader theme={theme}>
                            <div />
                            <FontAwesomeIcon
                                icon={faApple}
                                className="apple-logo"
                                style={{
                                    fontSize: '30px',
                                    color: theme?.card_text_color || '#000',
                                }}
                            />
                        </AppleHeader>
                        <AppleContent layout="highlight" className="highlight-layout">
                            <ArtistImage
                                src={item.thumbnail || 'https://via.placeholder.com/150'}
                                alt={item.title || item.artistName}
                            />
                            <ArtistInfo layout="highlight" theme={theme}>
                                <h3 className="artist-type">
                                    {item.appleType === 'album'
                                        ? item.albumName
                                        : item.artistName}
                                </h3>
                                <h2 className="artist-name">
                                    {item.appleType === 'album'
                                        ? item.artistName
                                        : 'Top Tracks'}
                                </h2>
                            </ArtistInfo>
                        </AppleContent>
                    </ApplePreviewCard>
                    <AnimatedApplePlayer show={showPlayer} style={{ marginTop: '20px' }}>
                        {renderAppleEmbed('475px')}
                    </AnimatedApplePlayer>
                </div>
            );
        }

        // Fallback layout
        return (
            <div>
                <ProductCard
                    key={item._id}
                    theme={theme}
                    className={cardClassName}
                    onClick={handleClick}
                    role="button"
                    tabIndex={0}
                >
                    <div className="product-content">
                        <div className="product-image">
                            <Avatar
                                shape="square"
                                size={90}
                                src={item.thumbnail || 'https://via.placeholder.com/150'}
                            />
                        </div>
                        <div className="product-info">
                            <h3 style={{ color: theme?.card_text_color }}>{item.title}</h3>
                            {item.artistName && (
                                <p style={{ color: theme?.card_text_color }}>{item.artistName}</p>
                            )}
                        </div>
                    </div>
                    <div
                        className="product-button"
                        style={{
                            backgroundColor: theme?.button_color,
                            minHeight: '55px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0 20px',
                            borderRadius:
                                theme?.button_style?.includes('slightly-rounded')
                                    ? '10px'
                                    : theme?.button_style?.includes('squared')
                                        ? '0'
                                        : '15px',
                            color: theme?.button_text_color || '#FFFFFF',
                        }}
                    >
                        <span className="button-text" style={{ color: theme?.button_text_color }}>
                            {item.buttonName || 'Open'}
                        </span>
                        <span className="button-icon">
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M5 12H19"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M12 5L19 12L12 19"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                    </div>
                </ProductCard>
                <AnimatedApplePlayer show={showPlayer} style={{ marginTop: '20px' }}>
                    {renderAppleEmbed('475px')}
                </AnimatedApplePlayer>
            </div>
        );
    };

    return renderCard();
};

export default AppleCardComponent;
